import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { EquipmentTypeSelect } from '@/components/EquipmentTypeSelect';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { allEquipmentTypesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { EquipmentTypeFamilyDao } from '@rock/db-admin';
import {
    type EquipmentTypeFamily,
    EquipmentTypeFamilyIdSchema,
    type EquipmentTypeId,
    isOfferableType,
    type Timestamp,
} from '@rock/types';
import { Plus } from 'lucide-react';
import { type FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const log = logger('FamilyDialog');
const equipmentTypeFamilyDao = new EquipmentTypeFamilyDao();

/** A family id reads like a catalog type's: lowercase words joined by dashes. */
const KEBAB_ID = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const MIN_MEMBERS = 2;

/**
 * One family of catalog types: the id an admin gives it (immutable once created), its name, and the
 * types one product may be filed under. A type belongs to at most one family, so a type another family
 * holds is not offered. Saving writes the doc whole; both matchers read it on their next pass.
 */
export const FamilyDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The family being edited; absent for a new one. */
    family?: EquipmentTypeFamily;
    families: EquipmentTypeFamily[];
}> = ({ open, onOpenChange, family, families }) => {
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);
    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [typeIds, setTypeIds] = useState<EquipmentTypeId[]>([]);
    const [busy, setBusy] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    useEffect(() => {
        if (!open) return;
        setId(family?.id ?? '');
        setName(family?.name ?? '');
        setTypeIds(family?.typeIds ?? []);
        setConfirmingDelete(false);
    }, [open, family]);

    const idProblem = family ? undefined : problemWithId(id.trim(), families);
    const offered = useMemo(() => {
        const taken = new Set(families.filter((other) => other.id !== family?.id).flatMap((other) => other.typeIds));
        return types.filter((type) => isOfferableType(type) && !taken.has(type.id));
    }, [types, families, family?.id]);
    const canSave = !busy && !idProblem && name.trim().length > 0 && typeIds.length >= MIN_MEMBERS;

    const save = async () => {
        const now = new Date().toISOString() as Timestamp;
        const familyId = family?.id ?? EquipmentTypeFamilyIdSchema.parse(id.trim());
        setBusy(true);
        try {
            await equipmentTypeFamilyDao.setEquipmentTypeFamily({
                object: 'equipment-type-family',
                schema: 1,
                id: familyId,
                name: name.trim(),
                typeIds,
                createdAt: family?.createdAt ?? now,
                updatedAt: now,
            });
            toast.success(family ? 'Family saved' : `Family ${familyId} created`);
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to save type family', { err, familyId });
            toast.error('Unable to save the family', { description: err instanceof Error ? err.message : undefined });
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (!family) return;
        setBusy(true);
        try {
            await equipmentTypeFamilyDao.deleteEquipmentTypeFamily(family.id);
            toast.success(`Family ${family.id} deleted`);
            onOpenChange(false);
        } catch (err) {
            log.error('Failed to delete type family', { err, familyId: family.id });
            toast.error('Unable to delete the family', { description: err instanceof Error ? err.message : undefined });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{family ? `Family ${family.id}` : 'New type family'}</DialogTitle>
                    <DialogDescription>
                        Catalog types one product may be filed under. A unit of one type may join a catalog model of
                        another in the family (flagged for review), and a recall listing one type reaches units of the
                        others by asking the owner the type.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="family-id">ID</Label>
                        <Input
                            id="family-id"
                            value={id}
                            onChange={(event) => setId(event.target.value)}
                            placeholder="ovens"
                            disabled={busy || !!family}
                            autoComplete="off"
                            className="font-mono"
                        />
                        <div className={idProblem && id ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}>
                            {idProblem && id
                                ? idProblem
                                : 'Lowercase words joined by dashes, like a type id. Cannot change later.'}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="family-name">Name</Label>
                        <Input
                            id="family-name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="Ovens"
                            disabled={busy}
                            autoComplete="off"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label>Types</Label>
                        <div className="flex flex-wrap items-center gap-1.5">
                            {typeIds.map((typeId) => (
                                <EquipmentTypeBadge
                                    key={typeId}
                                    typeId={typeId}
                                    title={typeTitles.get(typeId)}
                                    onRemove={() => setTypeIds((current) => current.filter((id) => id !== typeId))}
                                    disabled={busy}
                                />
                            ))}
                            <EquipmentTypeSelect
                                selected={typeIds}
                                types={offered}
                                onPick={(ids) => setTypeIds((current) => [...current, ...ids])}
                                disabled={busy}
                                trigger={
                                    <Button type="button" variant="outline" size="sm" disabled={busy}>
                                        <Plus className="h-4 w-4" />
                                        Add type
                                    </Button>
                                }
                            />
                        </div>
                        <div className="text-xs text-muted-foreground">
                            At least {MIN_MEMBERS} types. A type already in another family is not offered.
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    {family && (
                        <Button
                            type="button"
                            variant="destructive"
                            onClick={() => setConfirmingDelete(true)}
                            disabled={busy}
                        >
                            Delete…
                        </Button>
                    )}
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button type="button" onClick={() => void save()} disabled={!canSave}>
                        {busy ? 'Saving…' : family ? 'Save' : 'Create family'}
                    </Button>
                </DialogFooter>
            </DialogContent>
            <AlertDialog open={confirmingDelete} onOpenChange={(next) => !next && setConfirmingDelete(false)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this family?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Its types stop joining each other’s models and reaching each other’s recalls. Models already
                            joined across the family keep their units until each unit's next refresh.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                        <AlertDialogAction disabled={busy} onClick={() => void remove()}>
                            Delete family
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
};

function problemWithId(id: string, families: EquipmentTypeFamily[]): string | undefined {
    if (!id) return 'Enter an id';
    if (!KEBAB_ID.test(id) || !EquipmentTypeFamilyIdSchema.safeParse(id).success) {
        return 'Lowercase letters, digits and dashes only';
    }
    if (families.some((family) => family.id === id)) return 'A family with this id already exists';
    return undefined;
}
